import { logger } from '../services/logger';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from "../lib/db.ts";
import { useAuth } from "../lib/auth.tsx";
import { Check, X, Search, ArrowLeft, Filter, ShieldCheck, ShieldX, History, AlertTriangle } from 'lucide-react';
import { ConfirmationModal } from "../components/common/ConfirmationModal.tsx";
import { ClickableUserName } from "../components/common/ClickableUserName.tsx";

interface WalletTransaction {
  id: string;
  created_at: string;
  user_id: string;
  amount: number;
  type: string;
  status: 'pending' | 'completed' | 'unsuccessful';
  reference?: string;
  user_email?: string;
  user_full_name?: string;
  fica_verified?: boolean;
  processed_by_email?: string;
  processed_at?: string;
}

interface FilterParams {
  fullName?: string;
  email?: string;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  status?: 'pending' | 'completed' | 'unsuccessful' | null;
}

// Define the filtered withdrawal result type from Supabase
interface FilteredWithdrawal {
  id: string;
  created_at: string;
  user_id: string;
  amount: number;
  type: string;
  status: 'pending' | 'completed' | 'unsuccessful';
  reference?: string;
  email?: string;
  full_name?: string;
  fica_verified?: boolean;
  processed_by_email?: string;
  processed_at?: string;
}

interface AuditLogEntry {
  log_id: string;
  action: string;
  old_status: string;
  new_status: string;
  processed_by_email: string;
  fica_verified_at_time: boolean;
  notes: string;
  log_created_at: string;
}

export function AdminWithdrawals() {
  const [walletTransactions, setWalletTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterParams>({
    status: null
  });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [txToConfirm, setTxToConfirm] = useState<{ id: string; status: 'completed' | 'unsuccessful'; amount: number; email?: string; ficaVerified?: boolean } | null>(null);
  const [showAuditLog, setShowAuditLog] = useState<string | null>(null);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchWalletTransactions();
  }, [user, navigate]);

  const fetchWalletTransactions = async () => {
    try {
      setLoading(true);
      // Check if user is admin
      if (!user || user.app_metadata?.role !== 'admin') {
        navigate('/markets');
        return;
      }

      // Use the stored procedure to fetch filtered withdrawals
      const { data, error } = await supabase.rpc('get_filtered_withdrawals', {
        p_full_name: filters.fullName || null,
        p_email: filters.email || null,
        p_start_date: filters.startDate || null,
        p_end_date: filters.endDate || null,
        p_min_amount: filters.minAmount || null,
        p_max_amount: filters.maxAmount || null,
        p_status: filters.status || null
      });

      if (error) throw error;

      // Transform the data to match our component's expected format
      const transformedData: WalletTransaction[] = data.map((item: FilteredWithdrawal) => ({
        id: item.id,
        created_at: item.created_at,
        user_id: item.user_id,
        amount: item.amount,
        type: item.type,
        status: item.status,
        reference: item.reference,
        user_email: item.email,
        user_full_name: item.full_name,
        fica_verified: item.fica_verified ?? false,
        processed_by_email: item.processed_by_email,
        processed_at: item.processed_at
      }));

      setWalletTransactions(transformedData);
    } catch (error) {
      logger.error('Error fetching wallet transactions:', error);
      setError('Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (transactionId: string, newStatus: 'completed' | 'unsuccessful') => {
    // Find the transaction for showing details in confirmation
    const transaction = walletTransactions.find(tx => tx.id === transactionId);
    if (!transaction) return;
    
    // Set transaction details to confirm and open modal
    setTxToConfirm({
      id: transactionId,
      status: newStatus,
      amount: transaction.amount,
      email: transaction.user_email,
      ficaVerified: transaction.fica_verified
    });
    setIsConfirmOpen(true);
  };

  const fetchAuditLog = async (withdrawalId: string) => {
    try {
      setAuditLoading(true);
      const { data, error } = await supabase.rpc('get_withdrawal_audit_log', {
        p_withdrawal_id: withdrawalId
      });
      
      if (error) throw error;
      setAuditLog(data || []);
      setShowAuditLog(withdrawalId);
    } catch (err) {
      logger.error('Error fetching audit log:', err);
      setError('Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  };
  
  // New function to handle the actual update after confirmation
  const handleConfirmUpdateStatus = async () => {
    if (!txToConfirm) return;
    
    try {
      setLoading(true);
      setIsConfirmOpen(false);
      
      // Use the new database function to update the withdrawal status
      const { data, error } = await supabase.rpc('update_withdrawal_status', {
        p_transaction_id: txToConfirm.id,
        p_new_status: txToConfirm.status,
        p_notes: null
      });
      
      if (error) throw error;
      
      // Check if the operation was successful (new response format)
      if (data && typeof data === 'object' && 'success' in data) {
        if (!data.success) {
          setError(data.error || 'Failed to update withdrawal status');
          return;
        }
      }
      
      // Update local state
      setWalletTransactions(walletTransactions.map(transaction => 
        transaction.id === txToConfirm.id 
          ? { ...transaction, status: txToConfirm.status } 
          : transaction
      ));
      
      // Refresh transactions
      fetchWalletTransactions();
      
    } catch (error) {
      logger.error('Error updating withdrawal status:', error);
      setError('Failed to update withdrawal status');
    } finally {
      setLoading(false);
      setTxToConfirm(null);
    }
  };

  const handleFilterChange = (name: keyof FilterParams, value: string | number | null) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({ status: null });
    setSearchTerm('');
  };

  const filteredTransactions = searchTerm 
    ? walletTransactions.filter(transaction => 
        transaction.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.user_full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.reference?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : walletTransactions;

  // Input change handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>, key: keyof FilterParams) => {
    handleFilterChange(key, e.target.value || null);
  };

  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>, key: keyof FilterParams) => {
    handleFilterChange(key, e.target.value ? Number(e.target.value) : null);
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleFilterChange('status', e.target.value || null);
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wayja-teal"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8">
        <div className="text-center text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="flex items-center gap-3 mb-8">
        <Link to="/admin/dashboard" className="text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold">Withdrawals</h1>
      </div>
      
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Quick search by user email, name, reference or ID"
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        
        <button 
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50"
        >
          <Filter className="w-4 h-4" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>
      
      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                value={filters.fullName || ''}
                onChange={(e) => handleTextInputChange(e, 'fullName')}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                value={filters.email || ''}
                onChange={(e) => handleTextInputChange(e, 'email')}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                value={filters.status || ''}
                onChange={handleSelectChange}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="unsuccessful">Unsuccessful</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                value={filters.startDate || ''}
                onChange={(e) => handleTextInputChange(e, 'startDate')}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                value={filters.endDate || ''}
                onChange={(e) => handleTextInputChange(e, 'endDate')}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Amount</label>
              <input
                type="number"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                value={filters.minAmount || ''}
                onChange={(e) => handleNumberInputChange(e, 'minAmount')}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Amount</label>
              <input
                type="number"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                value={filters.maxAmount || ''}
                onChange={(e) => handleNumberInputChange(e, 'maxAmount')}
              />
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={clearFilters}
              className="mr-2 px-4 py-2 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50"
            >
              Clear Filters
            </button>
            <button
              onClick={fetchWalletTransactions}
              className="px-4 py-2 text-sm font-medium text-white rounded-md bg-wayja-teal hover:bg-opacity-90"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
      
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Request ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  FICA
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reference
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Processed By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTransactions.map((transaction) => (
                <tr key={transaction.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {transaction.id.slice(0, 8)}...
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(transaction.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <ClickableUserName
                      userId={transaction.user_id}
                      displayName={transaction.user_full_name || 'N/A'}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {transaction.user_email || transaction.user_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {transaction.fica_verified ? (
                      <span className="flex items-center gap-1 text-green-600" title="FICA Verified">
                        <ShieldCheck className="h-4 w-4" />
                        <span className="text-xs">Verified</span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-600" title="FICA Not Verified">
                        <ShieldX className="h-4 w-4" />
                        <span className="text-xs">Pending</span>
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" title={transaction.reference || 'No reference'}>
                    {transaction.reference || 'No reference'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    R {Math.abs(transaction.amount).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs 
                      ${transaction.status === 'completed' ? 'bg-green-100 text-green-800' : 
                        transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-red-100 text-red-800'}`
                      }
                    >
                      {transaction.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {transaction.processed_by_email ? (
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-600">{transaction.processed_by_email}</span>
                        {transaction.processed_at && (
                          <span className="text-xs text-gray-400">
                            {new Date(transaction.processed_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    ) : transaction.status === 'pending' ? (
                      <span className="text-gray-400 text-xs">-</span>
                    ) : (
                      <span className="text-amber-500 text-xs">No record</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex space-x-2">
                      {transaction.status === 'pending' && (
                        <>
                          {transaction.fica_verified ? (
                            <button
                              onClick={() => handleUpdateStatus(transaction.id, 'completed')}
                              className="text-green-600 hover:text-green-900"
                              title="Approve Withdrawal"
                            >
                              <Check className="h-5 w-5" />
                            </button>
                          ) : (
                            <button
                              className="text-gray-300 cursor-not-allowed"
                              title="Cannot approve: FICA not verified"
                              disabled
                            >
                              <Check className="h-5 w-5" />
                            </button>
                          )}
                          <button
                            onClick={() => handleUpdateStatus(transaction.id, 'unsuccessful')}
                            className="text-red-600 hover:text-red-900"
                            title="Reject Withdrawal"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </>
                      )}
                      {/* Audit log button for all transactions */}
                      <button
                        onClick={() => fetchAuditLog(transaction.id)}
                        className="text-gray-400 hover:text-gray-600"
                        title="View Audit History"
                      >
                        <History className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredTransactions.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-6 py-4 text-center text-sm text-gray-500">
                    No withdrawals found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => {
          setIsConfirmOpen(false);
          setTxToConfirm(null);
        }}
        onConfirm={handleConfirmUpdateStatus}
        title={txToConfirm?.status === 'completed' ? "Confirm Withdrawal Approval" : "Confirm Withdrawal Rejection"}
        message={
          <div className="space-y-2 text-sm">
            <p>Are you sure you want to {txToConfirm?.status === 'completed' ? 'approve' : 'reject'} this withdrawal?</p>
            <p><strong>Amount:</strong> R {txToConfirm?.amount ? Math.abs(txToConfirm.amount).toFixed(2) : 'N/A'}</p>
            {txToConfirm?.email && <p><strong>User:</strong> {txToConfirm.email}</p>}
            <p>
              <strong>FICA Status:</strong>{' '}
              {txToConfirm?.ficaVerified ? (
                <span className="text-green-600">✓ Verified</span>
              ) : (
                <span className="text-amber-600">⚠ Not Verified</span>
              )}
            </p>
            {txToConfirm?.status === 'completed' && !txToConfirm?.ficaVerified && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-red-600">
                  <strong>Warning:</strong> This user is NOT FICA verified. The approval will be blocked by the system.
                </p>
              </div>
            )}
            {txToConfirm?.status === 'unsuccessful' && (
              <p className="text-red-600">This will refund the amount back to the user's wallet.</p>
            )}
          </div>
        }
        confirmText={txToConfirm?.status === 'completed' ? "Approve Withdrawal" : "Reject Withdrawal"}
        cancelText="Cancel"
        isLoading={loading}
        variant={txToConfirm?.status === 'completed' ? "info" : "danger"}
      />

      {/* Audit Log Modal */}
      {showAuditLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold">Withdrawal Audit History</h3>
              <button
                onClick={() => {
                  setShowAuditLog(null);
                  setAuditLog([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
              {auditLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wayja-teal"></div>
                </div>
              ) : auditLog.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <History className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No audit history found for this withdrawal.</p>
                  <p className="text-xs mt-1">Audit logging was added recently. Older transactions may not have records.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {auditLog.map((entry) => (
                    <div key={entry.log_id} className="border-l-4 border-gray-300 pl-4 py-2">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          entry.action === 'approved' ? 'bg-green-100 text-green-800' :
                          entry.action === 'rejected' ? 'bg-red-100 text-red-800' :
                          entry.action === 'approval_blocked_fica' ? 'bg-amber-100 text-amber-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {entry.action === 'approval_blocked_fica' ? 'Blocked (No FICA)' : entry.action}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(entry.log_created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <p><strong>By:</strong> {entry.processed_by_email}</p>
                        <p><strong>Status:</strong> {entry.old_status} → {entry.new_status}</p>
                        <p>
                          <strong>FICA at time:</strong>{' '}
                          {entry.fica_verified_at_time ? (
                            <span className="text-green-600">Verified</span>
                          ) : (
                            <span className="text-amber-600">Not Verified</span>
                          )}
                        </p>
                        {entry.notes && <p><strong>Notes:</strong> {entry.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowAuditLog(null);
                  setAuditLog([]);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 